import React, { useCallback, useState } from "react";
import { WithApiFileLocationProvider } from "./WithApiFileLocationProvider";
import { fetchExploredDocumentPartLocation, fetchZipFileList } from "../../api/document/Location";
import { FileLocation } from "./FileLocationProvider";
import { DocumentLocationExplorer } from "./DocumentLocationExplorer";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import HeaderPanel from "../layout/HeaderPanel";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import FooterPanel from "../layout/FooterPanel";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { getFormattedPath, importDocumentRootAsync } from "../../redux/feature/document/document-slice";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/hooks";
import {
  selectBackendUrl,
  selectClientId,
  selectCpmUsername,
  selectTenantId,
} from "../../redux/feature/settings/configurations-slice";
import { CustomModal } from "../layout/ActionModal";
import { TextField } from "@fluentui/react/lib/TextField";
import { elaborationStatusItemIsBlocking } from "../../redux/feature/object/remote-elaborations-slice";
import { EVENT_CHANNEL_DEFAULT_TOPIC } from "../../utils/constants/eventChannel";
import { IMPORT_ELABORATION } from "../../utils/constants/notification";
import { ElaborationWrapper } from "../coobjects/ElaborationWrapper";
import { useFilteredElaborations } from "../generic/GenericElaborationSelector";

export interface ImportProps {
  currentlyOpenedDocumentId?: string;
}

export const ImportDocumentPanel: React.FunctionComponent<ImportProps> = (props) => {
  const username = useAppSelector(selectCpmUsername); // TEMP waiting for real login
  const backendUrl = useAppSelector(selectBackendUrl);
  const tenantId = useAppSelector(selectTenantId);
  const clientId = useAppSelector(selectClientId);
  const dispatch = useAppDispatch();

  const [state, setState] = useState<{
    zipFileItem: FlatPathTreeItem | undefined;
    loadedZip: boolean;
    folderItem: FlatPathTreeItem | undefined;
    loadedFolder: boolean;
    driveItemIdFolder: string;
    page: "ZIP" | "FOLDER" | "EXISTS" | "RENAME";
    folderName: string;
    fileExt: string;
    nameError: string | undefined;
    modifiedFields: boolean;
  }>({
    zipFileItem: undefined,
    loadedZip: false,
    folderItem: undefined,
    loadedFolder: false,
    page: "ZIP",
    driveItemIdFolder: undefined,
    folderName: undefined,
    fileExt: undefined,
    nameError: undefined,
    modifiedFields: true,
  });

  function onSelectZipFileItem(item: FlatPathTreeItem | undefined) {
    if (!item?.document || item.document.folder)
      setState((s) => ({ ...s, zipFileItem: undefined, folderName: item.text }));
    else {
      const fileExt = item.text.match(/(_CO\.zip)$/)?.length > 1 ? "_CO.zip" : ".zip";
      const fileName = item.text.replace(fileExt, "");

      setState((s) => ({ ...s, zipFileItem: item, folderName: fileName, fileExt: fileExt }));
    }
  }

  function onSelectFolder(item: FlatPathTreeItem | undefined) {
    setState({ ...state, folderItem: item });
  }

  async function importDocument(
    zipItem: FlatPathTreeItem,
    folderItem: FlatPathTreeItem,
    folderName: string,
    fileExt: string,
    currentlyOpenedDocumentId: string | undefined
  ) {
    const folders = await fetchExploredDocumentPartLocation(folderItem?.path ?? null);
    const isFolderPresent = folders.find((dp) => dp.name === folderName + fileExt.replace(".zip", ""));
    if (isFolderPresent) {
      setState((s) => ({ ...s, page: "EXISTS", driveItemIdFolder: isFolderPresent.driveItemId }));
    } else {
      importDocumentRootAsync({
        sharepointPath: getFormattedPath(folderItem?.path ?? null),
        zipFileName: folderName + fileExt,
        driveItemId: zipItem.document.driveItemId,
        username,
        tenantId,
        backendUrl,
        siteId: zipItem.document.siteId,
        dispatch,
        clientId,
        channelIdToNotify: currentlyOpenedDocumentId ?? EVENT_CHANNEL_DEFAULT_TOPIC,
      }).then(() => {
        setState((s) => ({
          ...s,
          page: "ZIP",
          loadedZip: false,
        }));
      });
    }
  }

  const backToFolderExplorerAndReset = () =>
    setState((s) => ({ ...s, page: "FOLDER", loadedFolder: false, folderItem: undefined }));
  const backToWarningModalAndReset = () =>
    setState((s) => ({ ...s, page: "EXISTS", loadedFolder: false, folderItem: undefined }));
  const backToZipExplorerAndReset = () =>
    setState((s) => ({ ...s, page: "ZIP", loadedZip: false, zipFileItem: undefined }));

  function onChangeFolderName(value: string) {
    const nameError = validateDocumentName(value);
    setState((s) => ({ ...s, folderName: value, nameError: nameError, modifiedFields: true }));
  }

  function validateDocumentName(str: string): string | undefined {
    const regex = /[\\/:*?"<>|]/; // regular expression to match unwanted characters
    if (regex.test(str)) {
      return formatMessage("cof.createdocument.documentname.invalidChar"); // return error message
    }
    return undefined; // no error, return undefined
  }

  const getExplorer = () => {
    if (state.page === "ZIP")
      return (
        <>
          <div
            style={{
              display: "flex",
              color: "var(--color-grey)",
              fontSize: "14px",
              padding: "5px",
            }}
          >
            {formatMessage("cof.import.zipinfo")}
          </div>
          <WithApiFileLocationProvider
            selectDepth="ITEM"
            cleanState={!state.loadedZip}
            getItems={async (path) => {
              const documentFolder = await fetchZipFileList(path);

              setState((s) => ({ ...s, loadedZip: true }));

              return documentFolder.map<FileLocation>((df) => {
                return {
                  key: df.driveItemId,
                  text: df.name,
                  path: df.path,
                  iconName: df.folder ? "fabricFolder" : "ZipFolder",
                  document: df,
                };
              });
            }}
          >
            <DocumentLocationExplorer onSelectItem={onSelectZipFileItem} page={"IMPORT"} />
          </WithApiFileLocationProvider>
        </>
      );
    if (state.page === "FOLDER")
      return (
        <>
          <div
            style={{
              display: "flex",
              color: "var(--color-grey)",
              fontSize: "14px",
              padding: "5px",
            }}
          >
            {formatMessage("cof.import.folderinfo")}
          </div>
          <WithApiFileLocationProvider
            selectDepth="FOLDER"
            cleanState={!state.loadedFolder}
            getItems={async (pathToFetch) => {
              setState((s) => ({ ...s, loadedFolder: true }));
              const folders = await fetchExploredDocumentPartLocation(pathToFetch);
              return folders.map<FileLocation>((df) => ({
                key: df.name,
                text: df.name,
              }));
            }}
          >
            <DocumentLocationExplorer onSelectItem={onSelectFolder} page={"IMPORT"} />
          </WithApiFileLocationProvider>
        </>
      );
    if (state.page === "RENAME") {
      return (
        <div className="ms-Grid" dir="ltr">
          <div className="ms-Grid-row">
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12">
              <TextField
                data-testid={"folderName"}
                label={formatMessage("cof.import.import.rename.foldername")}
                placeholder={formatMessage("cof.import.import.rename.foldername.placeholder")}
                value={state.folderName}
                onChange={(e) => onChangeFolderName((e.target as HTMLInputElement).value)}
                errorMessage={state.nameError}
              />
            </div>
          </div>
        </div>
      );
    }
    if (state.page === "EXISTS") {
      return (
        <CustomModal
          modalTitle={formatMessage("cof.import.title.selectfolder")}
          messageTitle={formatMessage("cof.import.import.title.packagealreadyexists")}
          message={formatMessage("cof.import.import.message.packagealreadyexists")}
          type="confirm"
          onHide={() => {}}
          actions={[
            {
              title: formatMessage("cof.base.cancel"),
              onClick: () => {
                setState((s) => ({
                  ...s,
                  page: "FOLDER",
                  folderName: state.zipFileItem.text.replace(".zip", ""),
                  modifiedFields: true,
                }));
              },
              variant: "white",
            },
            {
              title: formatMessage("cof.base.rename"),
              onClick: () => {
                setState((s) => ({ ...s, page: "RENAME", modifiedFields: false }));
              },
              variant: "blue",
            },
          ]}
        />
      );
    }
  };

  const getTitle = () => {
    if (state.page === "ZIP") return "cof.import.title.selectpackage";
    if (state.page === "FOLDER") return "cof.import.title.selectfolder";
    return "cof.import.title.renamefolder";
  };

  const addedElaborationsDetails = useAppSelector((store) => store.remoteElaborations.addedElaborationItemsList);
  const blockingElabFindFunction = useCallback(
    (item) => item.elaborationType === "IMPORT" && elaborationStatusItemIsBlocking(item, addedElaborationsDetails),
    [addedElaborationsDetails]
  );
  const blockingElaboration = useFilteredElaborations({
    findFunction: blockingElabFindFunction,
  });
  return (
    <ElaborationWrapper notificationKey={IMPORT_ELABORATION} elaborationItem={blockingElaboration}>
      <>
        <HeaderPanel data-testid={"header"} applyPadding>
          {formatMessage(getTitle())}
        </HeaderPanel>
        {getExplorer()}
        <FooterPanel>
          {state.page !== "ZIP" && (
            <PrimaryButton
              data-testid={"previous"}
              text={formatMessage("cof.base.previous")}
              style={{ float: "left", marginLeft: "15px" }}
              onClick={() => {
                if (state.page === "FOLDER") backToZipExplorerAndReset();
                else backToWarningModalAndReset();
              }}
            />
          )}
          {state.page !== "FOLDER" && state.page !== "RENAME" && (
            <PrimaryButton
              id="next_button"
              data-testid={"next"}
              disabled={!state.zipFileItem?.path.length}
              text={formatMessage("cof.base.next")}
              style={{ float: "right", marginLeft: "15px" }}
              onClick={backToFolderExplorerAndReset}
            />
          )}
          {state.page !== "ZIP" && (
            <PrimaryButton
              id="import_button"
              data-testid={"import"}
              disabled={!state.folderName || state.nameError != undefined || !state.modifiedFields}
              text={formatMessage("cof.base.import")}
              style={{ float: "right", marginLeft: "15px" }}
              onClick={() =>
                importDocument(
                  state.zipFileItem,
                  state.folderItem,
                  state.folderName,
                  state.fileExt,
                  props.currentlyOpenedDocumentId
                )
              }
            />
          )}
        </FooterPanel>
      </>
    </ElaborationWrapper>
  );
};
